import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

import { Roles } from '../../../_models/feature';
import { BaseServices } from '../../kuba.services';
import { SurveyListItemDto } from './surveys/models';
import { WorkEnvironmentSurveyService } from './surveys/services/work-environment-survey.service';

/** The sub-tabs of the work environment survey module. */
type SurveyTab = 'overview' | 'surveys' | 'analysis';

/**
 * Work environment survey — the tab host.
 *
 * Owns the sub-tabs and the survey selector, since both the overview and the
 * analysis read the same selected survey. Built the same way as
 * `EmployeeDialogMeetingsComponent`: an active-tab string, content switched
 * with `*ngIf`, no child routes and no resolvers.
 *
 * Ported from the AMIL prototype (https://github.com/Kuba-AS/Kuba-AMIL), whose
 * sidebar pages "Oversikt", "Undersøkelser" and "Analyse" become these tabs.
 * Its "Administrasjon" page is not carried over — departments and employees
 * are already managed elsewhere in this module — and its respondent page lives
 * outside the authenticated app, see `respondent-survey.component`.
 */
@Component({
  selector: 'work-environment-survey',
  templateUrl: './work-environment-survey.component.html',
  styleUrls: ['./work-environment-survey.component.scss'],
  standalone: false
})
export class WorkEnvironmentSurveyComponent implements OnInit, OnDestroy {
  //#region Variables
  private readonly businessId = BaseServices.BusinessId;

  activeTab: SurveyTab = 'overview';

  surveys: SurveyListItemDto[] = [];
  selectedSurveyId: number | null = null;

  isLoading = false;
  canManageSurveys = false;

  private subscriptions: Subscription[] = [];
  //#endregion

  constructor(
    private route: ActivatedRoute,
    private surveyService: WorkEnvironmentSurveyService
  ) {}

  //#region Lifecycle
  ngOnInit(): void {
    document.querySelector('body').classList.remove('opened');

    this.canManageSurveys = this.resolveManageAccess();

    this.subscriptions.push(
      this.route.queryParams.subscribe(params => {
        this.activeTab = this.normalizeTab(params['tab']);
      })
    );

    this.loadSurveys();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }
  //#endregion

  //#region Tabs
  switchTab(tab: SurveyTab): void {
    if (tab === 'surveys' && !this.canManageSurveys) {
      return;
    }

    this.activeTab = tab;
  }

  isTabActive(tab: SurveyTab): boolean {
    return this.activeTab === tab;
  }

  getTabClass(tab: SurveyTab): string {
    return this.isTabActive(tab) ? 'nav-link active' : 'nav-link';
  }

  /** The survey selector only applies to the result tabs. */
  get showSurveySelector(): boolean {
    return this.activeTab !== 'surveys' && this.surveys.length > 0;
  }

  private normalizeTab(tab: string | undefined): SurveyTab {
    switch (tab) {
      case 'surveys':
        return this.canManageSurveys ? 'surveys' : 'overview';
      case 'analysis':
        return 'analysis';
      default:
        return 'overview';
    }
  }

  /**
   * Who can create and edit surveys. Reading results is open to everyone the
   * tab is shown to; running a survey is an administrative act.
   */
  private resolveManageAccess(): boolean {
    const roleId = Number(BaseServices.roleId);
    return [Roles.ADMIN, Roles.PORTAL, Roles.EDITOR].includes(roleId);
  }
  //#endregion

  //#region Data
  /** Reloaded when the survey list tab reports a change. */
  loadSurveys(): void {
    this.isLoading = true;

    this.subscriptions.push(
      this.surveyService.getList(this.businessId).subscribe({
        next: surveys => {
          this.surveys = surveys;
          this.syncSelectedSurvey();
          this.isLoading = false;
        },
        error: () => {
          this.surveys = [];
          this.selectedSurveyId = null;
          this.isLoading = false;
        }
      })
    );
  }

  onSurveySelected(surveyId: number | null): void {
    this.selectedSurveyId = surveyId === null ? null : Number(surveyId);
  }

  /** Keeps the selection valid after the list changes — newest survey wins. */
  private syncSelectedSurvey(): void {
    const stillExists = this.surveys.some(
      survey => survey.Id === this.selectedSurveyId
    );

    if (stillExists) {
      return;
    }

    this.selectedSurveyId = this.surveys.length ? this.surveys[0].Id : null;
  }
  //#endregion
}
